import { useState } from "react";
import { Banner, Card, SectionTitle, formatBytes } from "../components/ui";
import { uploadToSignedUrl, type Api } from "../lib/api";

interface UploadRow {
  id: string;
  name: string;
  size: number;
  status: "uploading" | "done" | "error";
  pct: number;
  message?: string;
  ticketId?: string;
}

/** Log uploads — into the account's own isolated bucket. §4.4 */
export function Govern({ api }: { api: Api }) {
  const [rows, setRows] = useState<UploadRow[]>([]);

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const rowId = crypto.randomUUID();
      setRows((r) => [
        ...r,
        { id: rowId, name: file.name, size: file.size, status: "uploading", pct: 0 },
      ]);
      try {
        const { url, objectPath } = await api.uploadUrl(file.name, file.type);
        await uploadToSignedUrl(url, file, (pct) =>
          setRows((r) => r.map((row) => (row.id === rowId ? { ...row, pct } : row))),
        );
        const completed = await api.completeUpload(objectPath, file.size);
        setRows((r) =>
          r.map((row) =>
            row.id === rowId
              ? { ...row, status: "done", pct: 100, ticketId: completed.ticketId }
              : row,
          ),
        );
      } catch (err) {
        setRows((r) =>
          r.map((row) =>
            row.id === rowId
              ? { ...row, status: "error", message: (err as Error).message }
              : row,
          ),
        );
      }
    }
  }

  return (
    <div>
      <SectionTitle
        kicker="Log uploads · Understand"
        title="Upload support logs"
        desc="Files land only in your account's dedicated, isolated storage and are routed to PumaMesh support automatically. Bucket isolation is enforced server-side — you can only ever write to your own account."
      />

      <Card>
        <label
          className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded border border-dashed border-line-strong bg-bg-soft px-6 py-12 text-center transition hover:border-accent/60"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void handleFiles(e.dataTransfer.files);
          }}
        >
          <span className="text-sm font-medium text-ink">Drop log files here or browse</span>
          <span className="text-xs text-ink-dim">
            Each upload is write-only and scoped to a single object in your account's bucket.
          </span>
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />
        </label>
      </Card>

      {rows.length > 0 && (
        <div className="mt-6 space-y-3">
          {rows.map((row) => (
            <Card key={row.id} className="py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate text-sm text-ink">{row.name}</div>
                  <div className="text-xs text-ink-dim">{formatBytes(row.size)}</div>
                </div>
                <div className="w-40">
                  {row.status === "error" ? (
                    <Banner tone="error">{row.message ?? "Failed"}</Banner>
                  ) : (
                    <div className="h-2 overflow-hidden rounded-pill bg-bg-deep">
                      <div
                        className="h-full bg-accent transition-all"
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                  )}
                  <div className="mt-1 text-right text-xs text-ink-dim">
                    {row.status === "done" ? "Sent to support" : row.status === "error" ? "Error" : `${row.pct}%`}
                  </div>
                </div>
                {row.ticketId && (
                  <a
                    className="text-xs font-medium text-accent hover:underline"
                    href={`/app/tickets?ticket=${encodeURIComponent(row.ticketId)}`}
                  >
                    View support ticket
                  </a>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-ink-dim">
        Tracking an issue?{" "}
        <a href="https://support.pumamesh.com" className="text-accent-strong hover:underline">
          support.pumamesh.com
        </a>
      </p>
    </div>
  );
}
